/* eslint-disable no-restricted-globals */
import logger from './log'
import reportError from './report-error'

const DEFAULT_DESERIALIZER = JSON.parse
const DEFAULT_SERIALIZER = JSON.stringify
const EVENT_TYPE = 'storage'

/**
 * Handle saving of local data via html5 web-storage api.
 *
 */
export const webStorage = {
    /**
     * Check if the local storage is available.
     *
     * @returns {boolean}
     */
    isSupported: function () {
        // Additionally checking for the local storage methods to make sure we have the "real" api
        const supported =
            typeof localStorage !== 'undefined' &&
            localStorage &&
            typeof localStorage.getItem === 'function' &&
            typeof localStorage.setItem === 'function' &&
            typeof localStorage.removeItem === 'function' &&
            typeof localStorage.clear === 'function'
        return supported
    },

    /**
     * Create an event on the local storage and execute the given function on a local storage change.
     *
     * @param onChange The event triggered by a local storage change.
     */
    addStorageListener: function (onChange) {
        if (this.isSupported()) {
            window.addEventListener(
                EVENT_TYPE,
                event => {
                    if (event.storageArea === localStorage) {
                        // Don't fire for SessionStorage changes
                        typeof onChange === 'function' && onChange(event)
                    }
                },
                false
            )
        }
    },

    /**
     * Get content by key from local storage.
     *
     * @param {string} key - The key to look for
     * @returns {string|null} The content if found or null or undefined
     */
    read: function (key) {
        if (this.isSupported()) {
            return localStorage.getItem(key)
        }
        return undefined
    },

    /**
     * Retrieve and deserialize a stored serialized object value from local storage. The default deserializer
     * function is `JSON.parse`, but you may also provide your own deserializer function, which takes the serialized
     * string value as the only argument and returns a deserialized object. If there is no deserializer function, the
     * value from local storage is returned as is. When the key is not found in the local storage, undefined is
     * returned.
     *
     * @param {string} key - The local storage key
     * @param {Function} [deserializer=JSON.parse] - A function to deserialize a stored serialized object
     * @returns {Object} The stored object or undefined
     */
    readDeserialized: function (key, deserializer = DEFAULT_DESERIALIZER) {
        const serializedValue = this.read(key)
        if (serializedValue === null || serializedValue === undefined) {
            return undefined
        }
        try {
            return typeof deserializer === 'function' ? deserializer(serializedValue) : serializedValue
        } catch (error) {
            logger.error(error)
            return undefined
        }
    },

    /**
     * Store the given string value in local storage. In case an error (e.g. local storage quota exceeded) is reported,
     * the given value will not be stored.
     *
     * @param {string} key - The key to look for
     * @param {string} value - The content to be saved
     * @returns {void}
     */
    save: function (key, value) {
        if (this.isSupported()) {
            try {
                localStorage.setItem(key, value)
            } catch (error) {
                reportError(error, logger)
            }
        }
    },

    /**
     * Serialize a given object value and store the resulting string value in local storage. The default serializer
     * function is `JSON.stringify`, but you may also provide your own serializer function, which takes the value as
     * the only argument and returns a string representation of that value. If there is no serializer function or the
     * function result is not a string, the value is wrapped in a String object and its primitive value stored.
     *
     * @param {string} key - The local storage key
     * @param {Object} value - The object to be saved
     * @param {Function} [serializer=JSON.stringify] - A function to serialize the given value into a string value
     * @returns {void}
     */
    saveSerialized: function (key, value, serializer = DEFAULT_SERIALIZER) {
        const serializedValue = typeof serializer === 'function' ? serializer(value) : value
        const stringValue = typeof serializedValue === 'string' ? serializedValue : String(value).valueOf()
        this.save(key, stringValue)
    },

    /**
     * Remove content by key from local storage.
     *
     * @param {string} key - The key to look for
     * @returns {void}
     */
    remove: function (key) {
        if (this.isSupported()) {
            localStorage.removeItem(key)
        }
    },

    /**
     * Clear all content in local storage.
     *
     * @returns {void}
     */
    clear: function () {
        if (this.isSupported()) {
            localStorage.clear()
        }
    },

    /**
     * Returns the number of local storage entries.
     *
     * @returns {number}
     */
    count: function () {
        if (this.isSupported()) {
            return localStorage.length
        }
        return 0
    },

    /**
     * Returns an array of local storage keys.
     *
     * @returns {string[]}
     */
    keys: function () {
        const keys = []
        if (this.isSupported()) {
            for (const key in localStorage) {
                Object.prototype.hasOwnProperty.call(localStorage, key) && keys.push(key)
            }
        }
        return keys
    },

    /**
     * Runs the given callback function for each local storage key. The callback function gets one argument which is the
     * current key when looping through the local storage.
     *
     * @param {Function} callback
     */
    apply: function (callback) {
        if (callback && typeof callback === 'function' && this.isSupported()) {
            this.keys().forEach(key => callback(key))
        }
    },

    /**
     * Sums up and returns the current used local storage space in bytes (estimated).
     * Borrowed from https://stackoverflow.com/a/15720835.
     *
     * @returns {number}
     */
    inUse: function () {
        let total = 0
        if (this.isSupported()) {
            this.keys().forEach(key => {
                if (key) {
                    total += (localStorage[key].length + key.length) * 2
                }
            })
        }
        return total
    }
}
